import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RateLimitError } from "@atsready/shared/utils";
import type { CampaignOperationLocale } from "@atsready/shared";
import { listUsers } from "@/services/users";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { campaignEmailButtonClass, campaignEmailCopy } from "@/lib/campaign-email-copy";

export function CampaignEmailRecipients({
  selectedIds,
  onChange,
  disabled,
  locale,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
  locale: CampaignOperationLocale;
}) {
  const copy = campaignEmailCopy[locale];
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search === debouncedSearch) return;
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);
  const users = useQuery({
    queryKey: ["campaign-email-users", debouncedSearch, page],
    queryFn: async () => {
      try {
        return await listUsers({ search: debouncedSearch, page, pageSize: 25 });
      } catch (error) {
        if (error instanceof RateLimitError) setBlocked(true);
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: { skipGlobalErrorHandler: true },
    enabled: !blocked,
  });
  useEffect(() => {
    if (!(users.error instanceof RateLimitError)) return;
    const timer = setTimeout(
      () => setBlocked(false),
      Math.max(1, users.error.retryAfter ?? 60) * 1000,
    );
    return () => clearTimeout(timer);
  }, [users.error]);
  return (
    <section className="min-w-0 space-y-3" aria-labelledby="campaign-recipients-title">
      <h3 id="campaign-recipients-title" className="font-semibold">
        {copy.recipients}
      </h3>
      <p id="campaign-selection-help" className="text-sm text-muted-foreground">
        {copy.selectionHelp}
      </p>
      <label className="block space-y-1">
        <span>{copy.search}</span>
        <Input
          className="min-h-11"
          value={search}
          disabled={disabled || blocked}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <p>
        {copy.selected}: {selectedIds.length} / 100
      </p>
      {users.isFetching && <p role="status">{copy.loading}</p>}
      {users.isError && <p role="alert">{blocked ? copy.rate : copy.error}</p>}
      {users.data?.data.length === 0 && <p>{copy.emptyUsers}</p>}
      <div className="divide-y rounded-xl border">
        {users.data?.data.map((user) => (
          <label key={user.id} className="flex min-h-11 min-w-0 items-center gap-3 p-3">
            <input
              type="checkbox"
              className="h-5 w-5 shrink-0"
              checked={selectedIds.includes(user.id)}
              aria-describedby="campaign-selection-help"
              disabled={disabled || (!selectedIds.includes(user.id) && selectedIds.length >= 100)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selectedIds, user.id]
                    : selectedIds.filter((id) => id !== user.id),
                )
              }
            />
            <span className="min-w-0 break-words">
              {user.name ? `${user.name} · ` : ""}
              {user.email}
            </span>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          className={campaignEmailButtonClass}
          variant="outline"
          type="button"
          disabled={disabled || blocked || users.isFetching || page === 1}
          onClick={() => setPage(page - 1)}
        >
          {copy.previous}
        </Button>
        <Button
          className={campaignEmailButtonClass}
          variant="outline"
          type="button"
          disabled={
            disabled ||
            blocked ||
            users.isFetching ||
            !users.data ||
            page >= users.data.pagination.totalPages
          }
          onClick={() => setPage(page + 1)}
        >
          {copy.next}
        </Button>
        {users.isError && (
          <Button
            className={campaignEmailButtonClass}
            variant="outline"
            type="button"
            disabled={disabled || blocked || users.isFetching}
            onClick={() => {
              void users.refetch();
            }}
          >
            {copy.reload}
          </Button>
        )}
      </div>
    </section>
  );
}
